import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// Public site origin (frontend is served from this domain on Apache/cPanel).
export const SITE_URL = "https://www.galafarm.com.mk";
export const SITE_NAME = "Galafarm";
const DEFAULT_IMAGE = `${SITE_URL}/GalafarmLogo.png`;
const DEFAULT_DESCRIPTION =
  "Galafarm is a pharmaceutical and cosmetic company based in North Macedonia, offering high-quality healthcare products.";

const OG_LOCALE: Record<string, string> = {
  en: "en_US",
  mk: "mk_MK",
  sq: "sq_AL",
};

interface SeoProps {
  /** Page-specific title; " | Galafarm" is appended automatically. */
  title?: string;
  description?: string;
  /** Absolute image URL for social cards. */
  image?: string;
  type?: "website" | "article";
  /** JSON-LD structured data (single object or array). */
  jsonLd?: object | object[];
  /** Override canonical path; defaults to the current route. */
  canonicalPath?: string;
  lang?: string;
}

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  image,
  type = "website",
  jsonLd,
  canonicalPath,
  lang,
}) => {
  const location = useLocation();
  const path = canonicalPath ?? location.pathname;
  const url = `${SITE_URL}${path}`;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Галафарм`;
  const desc = description || DEFAULT_DESCRIPTION;
  const img = image || DEFAULT_IMAGE;
  const currentLang = lang || localStorage.getItem("lang") || "en";
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={OG_LOCALE[currentLang] || "en_US"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
